/**
 * MessageLink Component
 *
 * Purpose: Displays a rich preview of links shared in messages
 *
 * How it works:
 * 1. Fetches link metadata from local database using the message ID
 * 2. Shows a loading skeleton while data is being retrieved
 * 3. Renders link preview with domain, title, description and image when available
 *
 * Key features:
 * - Responsive design for different screen sizes
 * - Loading skeleton for better UX
 * - Properly formatted link preview with metadata
 */

import { cn } from "@/lib/utils";
import { getLinkByIdFromLocalDB } from "@/indexedDB/services/links";
import { MessageEntity } from "@/indexedDB/types";
import { useSafeLiveQuery } from "@/indexedDB/hooks/useSafeLiveQuery";

interface MessageLinkProps {
  message: MessageEntity;
}

// Constant to represent loading state
const INITIAL_VALUE = "INITIAL_VALUE";

export default function MessageLink({ message }: MessageLinkProps) {
  // Look up by _clientId first (stable across optimistic → server ID transition),
  // fall back to server id for messages that arrived via socket.
  const linkId = message._clientId || message.id;

  const linkDetails = useSafeLiveQuery(
    async () => {
      return (
        (await getLinkByIdFromLocalDB(linkId)) ??
        (linkId !== message.id
          ? await getLinkByIdFromLocalDB(message.id)
          : undefined)
      );
    },
    [linkId, message.content.link],
    INITIAL_VALUE,
  );

  // Show skeleton loader while fetching data
  if (linkDetails === INITIAL_VALUE) {
    return (
      <div className="flex w-full max-w-full flex-1 items-center gap-3 text-left lg:mx-auto lg:max-w-screen-3xl">
        <div className="flex min-w-0 flex-1 select-none flex-col rounded-lg bg-card p-2 shadow-sm transition-all">
          {/* Image skeleton - large and prominent */}
          <div className="mb-3 w-full overflow-hidden rounded-lg">
            <div className="aspect-video w-[400px] shrink-0 animate-pulse rounded-lg bg-foreground/10" />
          </div>
          {/* Link content skeleton */}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-3">
              {/* Title skeleton */}
              <div className="rounded mb-1 h-5 w-3/4 shrink-0 animate-pulse bg-foreground/10" />
            </div>
            {/* Description skeleton */}
            <div className="rounded mt-1 h-3 w-full shrink-0 animate-pulse bg-foreground/10" />
            <div className="rounded mt-1 h-3 w-2/3 shrink-0 animate-pulse bg-foreground/10" />
          </div>
        </div>
      </div>
    );
  }

  // Return null if no link details found
  if (!linkDetails) {
    return null;
  }

  return (
    <div className="flex w-full max-w-full flex-1 items-center gap-3 text-left lg:mx-auto lg:max-w-screen-3xl">
      <a
        href={linkDetails.url}
        target="_blank"
        rel="noreferrer noopener"
        className={cn(
          "flex min-w-0 flex-1 select-none flex-col rounded-md bg-card p-2 shadow-sm transition-all hover:bg-card/90",
        )}
      >
        {/* Link image - as the main focus */}
        {linkDetails.ogImage && (
          <div className="mb-3 w-full overflow-hidden rounded-lg">
            <img
              src={linkDetails.ogImage}
              alt={linkDetails.metaTitle || "Link preview image"}
              className="aspect-video w-full object-cover transition-all duration-300 hover:scale-105"
              loading="lazy"
            />
          </div>
        )}

        {/* Link content - supporting the image */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center gap-3">
            {!linkDetails.ogImage && linkDetails.faviconUrl && (
              <div className="size-10 shrink-0 overflow-hidden rounded-md border border-border/30 bg-background/50">
                <img
                  src={linkDetails.faviconUrl}
                  alt={linkDetails.metaTitle || "Link preview favicon"}
                  className="size-full object-contain p-1"
                  loading="lazy"
                />
              </div>
            )}
            {/* Link title */}
            {linkDetails.metaTitle && (
              <h3 className="mb-1 line-clamp-2 font-medium text-foreground">
                {linkDetails.metaTitle}
              </h3>
            )}
          </div>

          {/* Link description */}
          {linkDetails.metaDescription && (
            <p className="line-clamp-2 text-sm text-muted-2">
              {linkDetails.metaDescription}
            </p>
          )}
        </div>
      </a>
    </div>
  );
}
